/**
 * Kind icon + color configuration for Artifact kinds.
 * Covers all 8 kinds; unknown → custom.
 */
import {
  Target,
  BookOpen,
  Cpu,
  ShieldCheck,
  FileText,
  FlaskConical,
  PenSquare,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { slugify } from '@confluent/shared/lib/slug';

import type { Artifact } from '../../../generated/api/types.gen.js';

export type ArtifactKind = Artifact['kind'];
export type ArtifactScope = Artifact['scope'];

export const ARTIFACT_KINDS: readonly ArtifactKind[] = [
  'plan',
  'report',
  'spec',
  'code',
  'decision',
  'draft',
  'design',
  'custom',
];

interface KindConfig {
  icon: LucideIcon;
  /** Tailwind bg + text classes for the kind icon square */
  squareBg: string;
  squareText: string;
  /** Badge variant label */
  badgeVariant: 'accent' | 'teal' | 'purple' | 'warning' | 'success' | 'neutral';
  badgeLabel: string;
}

export const KIND_CONFIG: Record<string, KindConfig> = {
  plan: {
    icon: Target,
    squareBg: 'bg-accent-muted',
    squareText: 'text-accent-text',
    badgeVariant: 'accent',
    badgeLabel: 'Plan',
  },
  report: {
    icon: BookOpen,
    squareBg: 'bg-teal-muted',
    squareText: 'text-teal',
    badgeVariant: 'teal',
    badgeLabel: 'Report',
  },
  code: {
    icon: Cpu,
    squareBg: 'bg-purple-muted',
    squareText: 'text-purple',
    badgeVariant: 'purple',
    badgeLabel: 'Code',
  },
  decision: {
    icon: ShieldCheck,
    squareBg: 'bg-warning-muted',
    squareText: 'text-warning',
    badgeVariant: 'warning',
    badgeLabel: 'Decision',
  },
  spec: {
    icon: FlaskConical,
    squareBg: 'bg-success-muted',
    squareText: 'text-success',
    badgeVariant: 'success',
    badgeLabel: 'Spec',
  },
  draft: {
    icon: FileText,
    squareBg: 'bg-surface-alt',
    squareText: 'text-muted',
    badgeVariant: 'neutral',
    badgeLabel: 'Draft',
  },
  design: {
    icon: PenSquare,
    squareBg: 'bg-accent-muted',
    squareText: 'text-accent-text',
    badgeVariant: 'accent',
    badgeLabel: 'Design',
  },
  custom: {
    icon: Wrench,
    squareBg: 'bg-surface-alt',
    squareText: 'text-muted',
    badgeVariant: 'neutral',
    badgeLabel: 'Custom',
  },
};

export function getKindConfig(kind: string): KindConfig {
  return KIND_CONFIG[kind] ?? KIND_CONFIG.custom;
}

export function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return 'now';
}

/** Content size as a person reads it: `812 B`, `4.2 KB`. */
export function formatSize(content: string): string {
  const bytes = new TextEncoder().encode(content).length;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function exportAsMarkdown(artifact: { title: string; content: string }): void {
  const blob = new Blob([artifact.content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${slugify(artifact.title, { fallback: 'artifact' })}.md`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
